import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Focus, Play, Pause, RotateCcw, Timer, Lock, CheckCircle, List } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FocusPage() {
  const navigate = useNavigate();
  const { getAuthHeader, hasAccess } = useAuth();
  const [techniques, setTechniques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTimer, setActiveTimer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [completedSessions, setCompletedSessions] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState('');

  useEffect(() => {
    if (hasAccess) {
      fetchTechniques();
      const savedTasks = localStorage.getItem('focus-tasks');
      if (savedTasks) setTasks(JSON.parse(savedTasks));
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  useEffect(() => {
    localStorage.setItem('focus-tasks', JSON.stringify(tasks));
  }, [tasks]);

  useEffect(() => {
    let interval;
    if (isRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsRunning(false);
            setCompletedSessions(s => s + 1);
            // Play notification sound
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('Session klar!', { body: 'Bra jobbat! Ta en kort paus.' });
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, timeLeft]);

  const fetchTechniques = async () => {
    try {
      const response = await axios.get(`${API}/api/focus/techniques`, { headers: getAuthHeader() });
      setTechniques(response.data);
    } catch (error) {
      console.error('Error fetching focus techniques:', error);
    } finally {
      setLoading(false);
    }
  };

  const startTimer = (technique) => {
    setActiveTimer(technique);
    setTimeLeft(technique.duration_minutes * 60);
    setIsRunning(true);
    
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  const toggleTimer = () => {
    setIsRunning(!isRunning);
  };

  const resetTimer = () => {
    setTimeLeft(activeTimer.duration_minutes * 60);
    setIsRunning(false);
  };

  const exitTimer = () => {
    setActiveTimer(null);
    setIsRunning(false);
    setTimeLeft(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const addTask = () => {
    if (!newTask.trim()) return;
    setTasks([...tasks, { id: Date.now(), text: newTask, done: false }]);
    setNewTask('');
  };

  const toggleTask = (id) => {
    setTasks(tasks.map(t => t.id === id ? { ...t, done: !t.done } : t));
  };

  const deleteTask = (id) => {
    setTasks(tasks.filter(t => t.id !== id));
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="focus-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/calm')} className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-2xl font-light">Fokus</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center space-y-6">
            <Lock className="w-16 h-16 mx-auto text-muted-foreground/50" />
            <h2 className="font-heading text-2xl">Uppgradera för tillgång</h2>
            <p className="text-muted-foreground">Fokusverktyg ingår i premium.</p>
            <Button onClick={() => navigate('/subscription')} className="rounded-full" data-testid="upgrade-btn">
              Uppgradera nu
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="focus-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/calm')} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-2xl font-light">Fokus</h1>
          {completedSessions > 0 && (
            <div className="ml-auto flex items-center gap-2 text-sm text-primary">
              <CheckCircle className="w-4 h-4" />
              {completedSessions} session{completedSessions > 1 ? 'er' : ''} idag
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {!activeTimer ? (
          <>
            {/* Intro */}
            <div className="text-center space-y-3 animate-fade-in">
              <Focus className="w-12 h-12 mx-auto text-primary" />
              <h2 className="font-heading text-3xl font-light">Fokusverktyg</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Välj en fokusmetod nedan, starta timern och koncentrera dig på en uppgift i taget. 
                Skriv gärna ner vad du vill fokusera på först.
              </p>
            </div>

            {/* Task List */}
            <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4 animate-slide-up">
              <h3 className="font-heading text-lg font-medium flex items-center gap-2">
                <List className="w-5 h-5 text-primary" />
                Dagens uppgifter
              </h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addTask()}
                  placeholder="Lägg till uppgift..."
                  className="flex-1 p-3 rounded-xl border border-border/50 bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="task-input"
                />
                <Button onClick={addTask} className="rounded-xl" data-testid="add-task-btn">
                  Lägg till
                </Button>
              </div>
              {tasks.length > 0 ? (
                <div className="space-y-2">
                  {tasks.map((task) => (
                    <div
                      key={task.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                        task.done ? 'bg-primary/5 border-primary/20' : 'bg-secondary/30 border-border/50'
                      }`}
                    >
                      <button
                        onClick={() => toggleTask(task.id)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          task.done ? 'bg-primary border-primary' : 'border-muted-foreground/50'
                        }`}
                      >
                        {task.done && <CheckCircle className="w-4 h-4 text-primary-foreground" />}
                      </button>
                      <span className={`flex-1 ${task.done ? 'line-through text-muted-foreground' : ''}`}>
                        {task.text}
                      </span>
                      <button
                        onClick={() => deleteTask(task.id)}
                        className="text-muted-foreground hover:text-destructive text-sm"
                      >
                        Ta bort
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Inga uppgifter ännu. Lägg till vad du vill fokusera på idag.
                </p>
              )}
            </div>

            {/* Techniques Grid */}
            <section className="space-y-4">
              <h3 className="font-heading text-xl font-medium">Välj en fokusmetod</h3>
              {loading ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="h-40 bg-secondary/50 rounded-2xl animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {techniques.map((technique, index) => (
                    <button
                      key={technique.id}
                      onClick={() => startTimer(technique)}
                      className="bg-card rounded-2xl border border-border/50 p-6 text-left hover:shadow-lg transition-shadow animate-slide-up"
                      style={{ animationDelay: `${index * 0.1}s` }}
                      data-testid={`technique-${technique.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Timer className="w-6 h-6 text-primary" />
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {technique.duration_minutes} min
                        </span>
                      </div>
                      <h4 className="font-heading text-lg font-medium mt-4">{technique.title}</h4>
                      <p className="text-sm text-muted-foreground mt-2">{technique.description}</p>
                      <div className="mt-4 flex items-center gap-2 text-xs text-primary">
                        <Play className="w-3 h-3" />
                        Starta session
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </>
        ) : (
          /* Active Timer */
          <div className="max-w-md mx-auto text-center space-y-8 animate-fade-in">
            <div>
              <h3 className="font-heading text-2xl font-medium">{activeTimer.title}</h3>
              <p className="text-muted-foreground mt-2">{activeTimer.description}</p>
            </div>

            {/* Timer Display */}
            <div className="relative w-64 h-64 mx-auto">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="128"
                  cy="128"
                  r="120"
                  fill="none"
                  stroke="hsl(var(--secondary))"
                  strokeWidth="8"
                />
                <circle
                  cx="128"
                  cy="128"
                  r="120"
                  fill="none"
                  stroke="hsl(var(--primary))"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 120}
                  strokeDashoffset={2 * Math.PI * 120 * (1 - timeLeft / (activeTimer.duration_minutes * 60))}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-5xl font-heading font-medium">{formatTime(timeLeft)}</span>
                <span className="text-sm text-muted-foreground mt-2">
                  {isRunning ? 'Fokusera...' : timeLeft === 0 ? 'Klar!' : 'Pausad'}
                </span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex justify-center gap-4">
              <Button
                onClick={toggleTimer}
                size="lg"
                className="rounded-full w-16 h-16"
                data-testid="toggle-timer-btn"
              >
                {isRunning ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
              </Button>
              <Button
                onClick={resetTimer}
                size="lg"
                variant="outline"
                className="rounded-full w-16 h-16"
              >
                <RotateCcw className="w-6 h-6" />
              </Button>
            </div>

            {/* Tips */}
            {activeTimer.tips && activeTimer.tips.length > 0 && (
              <div className="bg-card rounded-2xl border border-border/50 p-6 text-left">
                <h4 className="font-medium mb-3">Tips under sessionen:</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {activeTimer.tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button
              onClick={exitTimer}
              variant="ghost"
              className="rounded-full"
            >
              Avsluta
            </Button>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-8">
          <p className="text-xs text-muted-foreground">
            Fokus är en muskel som kan tränas. Ju oftare du övar, desto lättare blir det att koncentrera dig.
          </p>
        </div>
      </main>
    </div>
  );
}
